import { SITE_ORIGIN, HT } from "../siteConfig";
import SEO from "../components/seo.jsx"
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// ── Swap these asset imports per page ──
import HeroImg       from "../assets/lllt-breadcrumb-bg.png";
import BreadcrumbImg from "../assets/lllt-breadcrumb-bg.png";
import SurgeonImg    from "../assets/drbhaveshgupta.jpeg";
import ProcedureImg  from "../assets/lllt-procedure.png";
import ResultImg     from "../assets/lllt-result.jpg";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

const PAGE = {
  hero: {
    eyebrow: "Hair Regeneration Therapy",
    heading: "Low Level Laser Therapy for Hair Loss",
    sub: "A non-surgical, painless treatment that uses targeted laser light to stimulate natural hair regrowth at Clinique Internationale.",
  },

  breadcrumb: [
    { label: "Home", href: "/" },
    { label: "Treatments", href: "/laser-light-therapy.php" },
    { label: "Low Level Laser Therapy", href: "/laser-light-therapy.php" },
  ],

  /* ── INTRO ── */
  intro: {
    eyebrow: "Overview",
    heading: "What is Low Level Laser Therapy?",
    paragraphs: [
      "Low Level Laser Therapy (LLLT) is a non-surgical method to treat hair loss. It has shown promising results in both male pattern and female pattern hair loss and has emerged as an effective alternative medical management option for hair loss patients.",
      "In this procedure, laser light is targeted to the hair loss region through a laser helmet, which the patient wears while seated comfortably in the treatment room. There is no cutting, no injections, and no discomfort involved at any stage.",
      "Each session lasts for 20–25 minutes. Patients usually require 2 sessions of laser therapy every week, for 6–8 weeks, to begin seeing optimal results, with continued improvement over the following months of consistent treatment.",
    ],
  },

  /* ── SCIENCE BEHIND LLLT ── */
  science: {
    eyebrow: "The Science",
    heading: "How LLLT Stimulates Hair Growth",
    intro: "LLLT works by delivering low-level red and near-infrared light directly to the scalp, where it is absorbed by cells to boost energy production and reactivate hair follicles that have become dormant.",
    growthFactors: [
      { code: "ATP",  name: "Cellular Energy Production", desc: "Laser light is absorbed by mitochondria in follicle cells, increasing ATP production and fuelling the cellular activity needed for hair growth." },
      { code: "BFC",  name: "Blood Flow & Circulation",    desc: "Photobiomodulation improves microcirculation in the scalp, increasing oxygen and nutrient delivery to weakened hair follicles." },
      { code: "ANA",  name: "Anagen Phase Extension",      desc: "Laser stimulation helps prolong the active growth (anagen) phase of the hair cycle, reducing premature shedding." },
      { code: "DHT",  name: "Reduced Follicle Sensitivity", desc: "LLLT may help lower localised sensitivity of follicles to DHT, one of the key hormonal drivers of pattern hair loss." },
      { code: "INF",  name: "Anti-Inflammatory Effect",     desc: "Low-level light reduces localised scalp inflammation, creating a healthier environment for follicles to function normally." },
    ],
    mechanisms: [
      { num: "01", title: "Light Absorption",     desc: "Red and near-infrared wavelengths penetrate the scalp and are absorbed by photoreceptors within hair follicle cells." },
      { num: "02", title: "Cellular Stimulation", desc: "Absorbed light energy activates cellular metabolism, encouraging dormant or miniaturised follicles back into active growth." },
      { num: "03", title: "Follicle Reactivation", desc: "Repeated sessions progressively reactivate weakened follicles, thickening existing strands and encouraging new growth over time." },
      { num: "04", title: "Sustained Regrowth",   desc: "Consistent twice-weekly sessions build cumulative benefit, leading to visibly denser, healthier hair with continued use." },
    ],
  },

  /* ── SURGEON ── */
  surgeon: {
    eyebrow: "Lead Surgeon",
    heading: "Low Level Laser Therapy at Clinique Internationale",
    paragraphs: [
      "At Clinique Internationale, LLLT sessions are supervised by Dr. Bhavesh Gupta and his expert team of specialists, using clinical-grade laser helmets calibrated for optimal wavelength and intensity.",
      "Dr. Gupta evaluates each patient's hair loss pattern, scalp condition, and overall hair health before recommending LLLT alone or in combination with PRP therapy, medical management, or hair transplant surgery for maximised results.",
      "With over 15 years of experience in hair restoration, Dr. Gupta has integrated laser therapy as a core component of non-surgical hair loss treatment at all Clinique Internationale centres across India.",
    ],
    tags: [ "Advanced Fellowship – Cosmetic Surgery", "AOSMI Member", "15+ Yrs Experience", "10,000+ Cases"],
  },

  /* ── COST ── */
  cost: {
    eyebrow: "Pricing",
    heading: `Low Level Laser Therapy Cost in ${HT.placeName}`,
    paragraphs: [
      "LLLT at Clinique Internationale is priced based on the number of sessions and the treatment plan recommended. A single session typically ranges from ₹1,500 to ₹4,000, with most patients recommended twice-weekly sessions over 6–8 weeks for optimal results.",
      "Multiple session packages are available at discounted rates. Consult with our specialists for a personalised treatment plan and accurate pricing.",
    ],
    cities: [
      { city: "Mumbai",    range: "₹2,000 – ₹4,000 / session", href: "/",      surgeon: "Dr. Bhavesh Gupta"  },
      { city: "Bangalore", range: "₹1,500 – ₹3,500 / session", href: "https://bangalore.hairclinique.in/",              surgeon: "Dr. Idris MD"          },
      { city: "Pune",      range: "₹1,500 – ₹3,500 / session", href: "https://pune.hairclinique.in/", surgeon: "Dr. Ajinkya Patil"  },
      { city: "Shirdi",    range: "₹1,500 – ₹3,500 / session", href: "https://shirdi.hairclinique.in/",                    surgeon: "Dr. Pranay Thakur"    },
    ],
  },

  /* ── PROCEDURE STEPS ── */
  procedure: {
    eyebrow: "The Procedure",
    heading: "How is Laser Therapy Performed?",
    intro: "LLLT is a straightforward, in-clinic procedure that takes approximately 20–25 minutes per session. It is completely painless, non-invasive, and requires no downtime.",
    steps: [
      {
        num: "01",
        title: "Scalp Assessment",
        desc: "The specialist examines the patient's scalp and current hair density to confirm LLLT is suitable and to set a baseline for tracking progress.",
      },
      {
        num: "02",
        title: "Helmet Fitting",
        desc: "The patient is seated comfortably and fitted with a clinical-grade laser helmet positioned to evenly cover the areas of thinning or hair loss.",
      },
      {
        num: "03",
        title: "Laser Activation",
        desc: "The helmet is switched on, delivering low-level red and near-infrared light directly to the scalp for the duration of the session.",
      },
      {
        num: "04",
        title: "20–25 Minute Session",
        desc: "The patient relaxes while wearing the helmet no discomfort, heat, or sensation is felt during the treatment.",
      },
      {
        num: "05",
        title: "Twice-Weekly Schedule",
        desc: "Patients typically attend 2 sessions per week for 6–8 weeks to build up the cumulative effect needed for visible results.",
      },
      {
        num: "06",
        title: "Progress Review",
        desc: "The specialist reviews scalp photographs and hair density at regular intervals, adjusting the treatment plan as needed.",
      },
    ],
  },

  /* ── BENEFITS ── */
  benefits: {
    eyebrow: "Why Choose LLLT",
    heading: "Benefits of Low Level Laser Therapy",
    items: [
      { title: "Completely Non-Invasive", desc: "LLLT involves no cuts, no injections, and no anaesthesia just targeted light delivered through a comfortable laser helmet." },
      { title: "Pain-Free",               desc: "Patients feel nothing during treatment no heat, no discomfort making it suitable for those who prefer to avoid needles." },
      { title: "Zero Downtime",           desc: "Patients can resume all normal activities immediately after each session, with no recovery period required." },
      { title: "Suits Male & Female Loss", desc: "LLLT has shown effective results for both male pattern and female pattern hair loss, unlike some treatments limited to one gender." },
      { title: "Complements Other Treatments", desc: "LLLT pairs well with PRP therapy and hair transplant surgery, supporting stronger overall regrowth outcomes." },
    ],
  },

  /* ── WHO IS A CANDIDATE ── */
  candidates: {
    eyebrow: "Is LLLT Right for You?",
    heading: "Who is a Good Candidate for LLLT?",
    ideal: [
      "Early to moderate stages of androgenetic alopecia (male or female pattern hair loss)",
      "Individuals experiencing diffuse thinning across the scalp",
      "Patients seeking a completely non-invasive, pain-free hair loss treatment",
      "Those looking to complement or maintain results from PRP or hair transplant surgery",
      "Individuals wanting a low-risk option with no downtime or recovery period",
    ],
    notIdeal: [
      "Patients with very advanced (Norwood Grade 6–7) hair loss where follicles are completely absent",
      "Individuals with active scalp infections, open wounds, or inflammatory skin conditions",
      "Patients with photosensitivity disorders or a history of adverse reactions to light-based treatments",
      "Those expecting immediate results LLLT requires consistent sessions over several weeks",
    ],
  },

  /* ── FAQs ── */
  faqs: [
    {
      q: "How many LLLT sessions do I need?",
      a: "Most patients begin with 2 sessions per week for 6–8 weeks to see optimal initial results, followed by maintenance sessions to sustain hair density. The exact schedule depends on the degree of hair loss and individual response.",
    },
    {
      q: "Is Low Level Laser Therapy painful?",
      a: "No. LLLT is completely painless. The patient simply wears a laser helmet for 20–25 minutes there is no heat, no needles, and no discomfort at any point during the session.",
    },
    {
      q: "When will I see results from LLLT?",
      a: "Most patients begin to notice reduced hair shedding within the first few weeks. Visible improvement in hair density and thickness typically becomes apparent after 6–8 weeks of consistent, twice-weekly sessions.",
    },
    {
      q: "Can LLLT be combined with PRP or hair transplant surgery?",
      a: "Yes. LLLT is frequently used alongside PRP therapy and after hair transplant surgery to support healthier regrowth and help maintain long-term results.",
    },
    {
      q: "Are there any side effects of laser therapy?",
      a: "LLLT is considered very safe with minimal side effects. Some patients may experience mild scalp warmth or tingling during a session, which resolves immediately after treatment.",
    },
    {
      q: "Is Low Level Laser Therapy a permanent solution for hair loss?",
      a: "LLLT is not a permanent cure but an effective ongoing therapy. It helps slow progression, improve density, and extend the growth phase of hair. Regular sessions help sustain results long-term.",
    },
  ],

  /* ── CTA ── */
  cta: {
    eyebrow: "Take the First Step",
    heading: "Restore Your Hair with Laser Therapy",
    sub: "Book a personalised consultation with our specialists at Clinique Internationale. Get a complete scalp assessment and a treatment plan designed specifically for your hair loss pattern.",
    primary:   { label: "Book Consultation", href: "https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20Low%20Level%20Laser%20Therapy%20for%20Hair%20Loss" },
    secondary: { label: "View All Treatments", href: "/laser-light-therapy.php" },
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS  (identical across all treatment pages)
============================================================ */
function Eyebrow({ children, light = false, align = "left" }) {
  return (
    <p style={cinzel} className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center gap-2.5 ${align === "center" ? "justify-center" : "justify-start"} ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}>
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false, align = "left" }) {
  return (
    <h2 style={cinzel} className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] uppercase tracking-[0.05em] mb-8 leading-tight ${align === "center" ? "text-center" : "text-left"} ${light ? "text-white" : "text-[#1a0f02]"}`}>
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

function FramedImage({ src, alt, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" />
      <span className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/15 rounded-2xl pointer-events-none" />
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-xl overflow-hidden transition-colors duration-300 ${open ? "border-[#c9a84c]/40 bg-[#c9a84c]/[0.04]" : "border-[#c9a84c]/15 bg-white"}`}>
      <button className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left" onClick={() => setOpen(p => !p)}>
        <span style={cinzel} className="text-[13px] font-bold tracking-[0.04em] text-[#1a0f02]">{q}</span>
        <span className={`flex-shrink-0 w-7 h-7 rounded-full border border-[#c9a84c]/35 flex items-center justify-center transition-transform duration-300 ${open ? "rotate-45 bg-[#c9a84c] border-[#c9a84c]" : ""}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M2 6h8" stroke={open ? "#1a0f02" : "#c9a84c"} strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open && <div className="px-5 sm:px-6 pb-5"><p className="text-[13.5px] leading-[1.8] text-[#6b5535]">{a}</p></div>}
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function LowLevelLaserTherapy() {
  const pageRef     = useRef(null);
  const heroEyeRef  = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef  = useRef(null);
  const secRefs     = useRef([]);
  const addSection  = (el) => el && !secRefs.current.includes(el) && secRefs.current.push(el);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current,  { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7,  ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1  });
      gsap.fromTo(heroSubRef.current,  { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7,  ease, delay: 0.18 });
      secRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(el, { opacity: 0, y: 36 }, {
          opacity: 1, y: 0, duration: 0.75, ease,
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" },
        });
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  const wrap = "w-full px-5 sm:px-10 lg:px-16 xl:px-24";
  const sec  = "py-16 sm:py-20 md:py-24";

  return (
    <>
    <SEO
  title="Low Level Laser Therapy for Hair Loss | Clinique Internationale"
  description="Non-invasive Low Level Laser Therapy (LLLT) for hair loss at Clinique Internationale, led by Dr. Bhavesh Gupta. Stimulate growth with proven laser technology."
  url={`${SITE_ORIGIN}/laser-light-therapy.php`}
/>
      <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap" rel="stylesheet" />

<div ref={pageRef} className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20" style={{ fontFamily: "'Raleway', sans-serif" }}>

  {/* ══════════════════════════════════════
      HERO
  ══════════════════════════════════════ */}
  <div className="relative w-full h-[200px] xs:h-[280px] sm:h-[340px] md:h-[700px] flex items-center justify-end overflow-hidden">
    <img className="absolute inset-0 w-full h-full object-cover object-[center_20%] md:object-center" src={HeroImg} alt={PAGE.hero.heading} />
    <div className="absolute inset-0 " />
    <div className="absolute inset-0 opacity-40 pointer-events-none"
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")" }}
    />
    <div className="relative z-10 text-right px-4 sm:px-10 md:px-16 max-w-[85%] sm:max-w-xl ml-auto mr-0">
      <p ref={heroEyeRef} style={cinzel} className="text-[5px] xs:text-[9px] sm:text-[10.5px] tracking-[0.2em] xs:tracking-[0.24em] sm:tracking-[0.32em] uppercase text-white mb-2 sm:mb-4 flex items-center justify-end gap-2 sm:gap-3">
        <span className="w-4 sm:w-7 h-px bg-white" />{PAGE.hero.eyebrow}<span className="w-4 sm:w-7 h-px bg-white" />
      </p>
      <h1 ref={heroHeadRef} style={cinzel} className="font-black text-[0.8rem] xs:text-[1.8rem] sm:text-[2.8rem] md:text-[3.4rem] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-white mb-2 sm:mb-3 leading-[1.15] sm:leading-tight max-w-[140px] xs:max-w-[240px] sm:max-w-none ml-auto">
        {PAGE.hero.heading}
      </h1>
      <p ref={heroSubRef} className="text-[6px] xs:text-[12px] sm:text-[15px] text-white/70 leading-relaxed max-w-[140px] xs:max-w-[240px] sm:max-w-none ml-auto">{PAGE.hero.sub}</p>
    </div>
  </div>


        {/* ── BREADCRUMB ── */}
        <div className="relative w-full overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover" src={BreadcrumbImg} alt="" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0e0905]/88" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />
          <div className={`relative ${wrap} py-3.5 flex flex-wrap items-center gap-2`}>
            {PAGE.breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#c9a84c]/30 text-xs">›</span>}
                {b.href
                  ? <a href={b.href} style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">{b.label}</a>
                  : <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">{b.label}</span>}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-4xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.intro.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.intro.heading}</SectionHeading>
            {PAGE.intro.paragraphs.map((p, i) => (
              <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">{p}</p>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            SCIENCE mechanisms + factors (dark band)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.science.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.science.heading}</SectionHeading>
              <p className="text-[14px] leading-[1.85] text-white/55 max-w-3xl mx-auto text-center mb-12">{PAGE.science.intro}</p>

              {/* Effect cards */}
              <p style={cinzel} className="text-[10px] tracking-[0.22em] uppercase text-[#c9a84c]/60 text-center mb-5">Key Biological Effects of LLLT</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-14">
                {PAGE.science.growthFactors.map((g) => (
                  <div key={g.code} className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 hover:bg-[#c9a84c]/[0.08] hover:border-[#c9a84c]/30 transition-colors text-center">
                    <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                    <div style={cinzel} className="font-black text-[1.5rem] leading-none mb-1 bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">{g.code}</div>
                    <div style={cinzel} className="text-[9px] tracking-[0.1em] uppercase text-[#c9a84c]/70 mb-3 leading-snug">{g.name}</div>
                    <p className="text-[11.5px] leading-[1.7] text-white/50">{g.desc}</p>
                  </div>
                ))}
              </div>

              {/* Mechanism cards */}
              <p style={cinzel} className="text-[10px] tracking-[0.22em] uppercase text-[#c9a84c]/60 text-center mb-5">Mechanisms of Action</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {PAGE.science.mechanisms.map((m) => (
                  <div key={m.num} className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/18 rounded-2xl p-6 hover:border-[#c9a84c]/35 transition-colors">
                    <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                    <span style={cinzel} className="font-black text-[2rem] leading-none text-[#c9a84c]/18 select-none block mb-3">{m.num}</span>
                    <div style={cinzel} className="text-[12px] font-bold tracking-[0.06em] uppercase text-white/85 mb-2">{m.title}</div>
                    <p className="text-[12.5px] leading-[1.75] text-white/50">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            SURGEON
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <FramedImage src={SurgeonImg} alt="Dr. Bhavesh Gupta overseeing laser therapy" className="w-full h-auto object-contain" />
            <div>
              <Eyebrow>{PAGE.surgeon.eyebrow}</Eyebrow>
              <SectionHeading>{PAGE.surgeon.heading}</SectionHeading>
              {PAGE.surgeon.paragraphs.map((p, i) => (
                <p key={i} className="text-[14px] leading-[1.9] text-[#3a2e1e] mb-4">{p}</p>
              ))}
              <div className="flex flex-wrap gap-2 mt-5">
                {PAGE.surgeon.tags.map(t => (
                  <span key={t} style={cinzel} className="text-[9.5px] tracking-[0.12em] uppercase text-[#a07828] border border-[#c9a84c]/30 rounded px-3 py-1">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            COST + CITY CARDS
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">{PAGE.cost.eyebrow}</Eyebrow>
              <SectionHeading align="center">{PAGE.cost.heading}</SectionHeading>
              {PAGE.cost.paragraphs.map((p, i) => (
                <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-4">{p}</p>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto mt-8">
                {PAGE.cost.cities.map((c, i) => (
                  <a key={i} href={c.href}
                    className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(90,60,10,0.1)] transition-all duration-300 block text-center group">
                    <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                    <div style={cinzel} className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-1 group-hover:text-[#a07828] transition-colors">{c.city}</div>
                    <div className="text-[11.5px] text-[#6b5535] mb-3">{c.surgeon}</div>
                    <div style={cinzel} className="font-black text-[1rem] leading-snug bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">{c.range}</div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            PROCEDURE STEPS (image + 2-col grid)
        ══════════════════════════════════════ */}
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.procedure.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.procedure.heading}</SectionHeading>
              <p className="text-[14px] leading-[1.9] text-white/55 max-w-3xl mx-auto text-center mb-10">{PAGE.procedure.intro}</p>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
                <FramedImage src={ProcedureImg} alt="Laser helmet hair therapy session" className="h-[260px] sm:h-[340px] lg:h-full lg:sticky lg:top-24" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PAGE.procedure.steps.map((s) => (
                    <div key={s.num} className="relative overflow-hidden bg-white/[0.04] border border-[#c9a84c]/15 rounded-2xl p-5 hover:bg-[#c9a84c]/[0.07] hover:border-[#c9a84c]/30 transition-colors">
                      <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <span style={cinzel} className="font-black text-[1.3rem] leading-none text-[#c9a84c]/25 select-none flex-shrink-0">{s.num}</span>
                        <div style={cinzel} className="text-[11.5px] font-bold tracking-[0.05em] uppercase text-white/90">{s.title}</div>
                      </div>
                      <p className="text-[12.5px] leading-[1.75] text-white/55">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            BENEFITS (result image + cards)
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection}>
            <Eyebrow align="center">{PAGE.benefits.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.benefits.heading}</SectionHeading>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10 items-center">
              <FramedImage src={ResultImg} alt="Hair regrowth result after laser therapy" className="h-[260px] sm:h-[320px]" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PAGE.benefits.items.map((b, i) => (
                  <div key={i} className="bg-white border border-[#c9a84c]/18 rounded-2xl p-5 relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(90,60,10,0.08)] transition-all duration-300">
                    <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                    <div className="flex items-center gap-2.5 mb-2">
                      <span style={cinzel} className="text-[11px] font-black text-[#c9a84c]">{String(i + 1).padStart(2, "0")}</span>
                      <div style={cinzel} className="text-[11.5px] font-bold tracking-[0.05em] uppercase text-[#1a0f02]">{b.title}</div>
                    </div>
                    <p className="text-[12.5px] leading-[1.75] text-[#6b5535]">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            WHO IS A CANDIDATE (dark band)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.candidates.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.candidates.heading}</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">

                {/* Ideal candidates */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/18 rounded-2xl p-7">
                  <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l4 4 6-6" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <span style={cinzel} className="text-[12px] font-bold uppercase tracking-[0.06em] text-white/85">Ideal Candidates</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {PAGE.candidates.ideal.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13px] leading-[1.7] text-white/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] flex-shrink-0 mt-1.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Not ideal */}
                <div className="relative overflow-hidden bg-white/[0.03] border border-white/10 rounded-2xl p-7">
                  <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3v5M7 10v1" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round"/></svg>
                    </div>
                    <span style={cinzel} className="text-[12px] font-bold uppercase tracking-[0.06em] text-white/85">May Not Be Suitable</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {PAGE.candidates.notIdeal.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13px] leading-[1.7] text-white/55">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400/60 flex-shrink-0 mt-1.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            FAQs
        ══════════════════════════════════════ */}
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow align="center">Common Questions</Eyebrow>
              <SectionHeading align="center">Frequently Asked Questions</SectionHeading>
              <div className="max-w-3xl mx-auto flex flex-col gap-3">
                {PAGE.faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.cta.eyebrow}</Eyebrow>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5">{PAGE.cta.heading}</h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a href={PAGE.cta.primary.href} target="_blank" rel="noopener noreferrer"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block">
                {PAGE.cta.primary.label}
              </a>
              <a href={PAGE.cta.secondary.href} style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block">
                {PAGE.cta.secondary.label}
              </a>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}